import { useState } from 'react';

interface SubmitCampModalProps {
  isOpen: boolean;
  onClose: () => void;
  formUrl: string;
}

export default function SubmitCampModal({ isOpen, onClose, formUrl }: SubmitCampModalProps) {
  const [formData, setFormData] = useState({
    campName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    city: '',
    ageRange: '',
    priceRange: '',
    duration: '',
    category: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({
            campName: '',
            contactName: '',
            email: '',
            phone: '',
            website: '',
            city: '',
            ageRange: '',
            priceRange: '',
            duration: '',
            category: '',
            description: ''
          });
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-[#E91E8C] to-[#D946A6] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-bold text-white">Submit Your Camp</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors">
            <i className="ri-close-line text-2xl text-white"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6" data-readdy-form id="submit-camp-form">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Camp Name *</label>
              <input
                type="text"
                name="campName"
                value={formData.campName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                placeholder="Enter camp name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Contact Name *</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="https://yourcamp.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="e.g., Lawrenceville"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Age Range *</label>
                <input
                  type="text"
                  name="ageRange"
                  value={formData.ageRange}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="e.g., 5-12"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Price Range *</label>
                <input
                  type="text"
                  name="priceRange"
                  value={formData.priceRange}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                  placeholder="e.g., $200-$300/week"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Duration *</label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                >
                  <option value="">Select duration</option>
                  <option value="full-day">Full-Day</option>
                  <option value="half-day">Half-Day</option>
                  <option value="overnight">Overnight</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
              >
                <option value="">Select category</option>
                <option value="sports">Sports</option>
                <option value="stem">STEM / Tech</option>
                <option value="arts">Arts / Creative</option>
                <option value="outdoor">Outdoor / Nature</option>
                <option value="faith">Faith-based</option>
                <option value="special-needs">Special Needs / Inclusive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Camp Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm resize-none"
                placeholder="Tell us about your camp..."
              ></textarea>
              <div className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</div>
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mt-4 p-4 bg-[#F5C518]/10 border border-[#F5C518]/30 rounded-lg flex items-center gap-2 text-[#3D1A6E]">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-check-line text-lg"></i>
              </div>
              <span className="text-sm font-semibold">Thank you! Your camp has been submitted successfully.</span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-error-warning-line text-lg"></i>
              </div>
              <span className="text-sm font-semibold">Something went wrong. Please try again.</span>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-[#3D1A6E] text-[#3D1A6E] font-semibold rounded-lg hover:bg-[#3D1A6E]/5 transition-colors text-sm whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white font-semibold rounded-lg hover:from-[#D91E8C] hover:to-[#C946A6] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap shadow-lg"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Camp'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}